// features/vehicles/VehiclePage.tsx
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    Page,
    IndexTable,
    Card,
    Button,
    IndexFilters,
    useIndexResourceState,
    Text,
    useBreakpoints,
    IndexFiltersMode,
    useSetIndexFiltersMode,
    type IndexFiltersProps,
    type TabProps,
    ChoiceList,
    AppProvider,
    FormLayout,
    TextField,
    Banner,
    InlineError,
    Checkbox,
    Select,
} from '@shopify/polaris';
import { SafeAppBridgeModal as Modal, SafeAppBridgeTitleBar as TitleBar } from '../../app/components/SafeAppBridge';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import agent from '../../app/api/agent';
import type { Vehicle, VehicleCreate, VehicleEdit } from '../../app/models/Vehicle';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldStateKey,
    getLegacyVehicleFieldValue,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';
import {
    FITMENT_CATALOG_LABEL_CHANGED,
    getCachedFitmentCatalogLabel,
    getStoredFitmentCatalogLabel,
    setCachedFitmentCatalogLabel,
} from '../../app/utils/fitmentCatalogLabel';
import { MiniBanner } from '../components/MiniBanner';
import { DeleteIcon } from '@shopify/polaris-icons';
import enTranslations from '@shopify/polaris/locales/en.json';
import vehicleEmptyStateImage from '../../assets/dashboard/polaris-fitment.png';

// Satisfy Polaris' {[key:string]: unknown} constraint
type VehicleRow = Vehicle & { [key: string]: unknown };
type SelectOption = { label: string; value: string };
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];

type FieldErrors = {
    yearFrom?: string;
    yearTo?: string;
    make?: string;
    model?: string;
};

const DEBOUNCE_MS = 500;
const YEAR_MIN = 1886;
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_MAX_DYNAMIC = CURRENT_YEAR + 1; // allow next model year

const isFourDigits = (s: string) => /^\d{4}$/.test(s);

const inYearBounds = (y: number) =>
    y >= YEAR_MIN && y <= YEAR_MAX_DYNAMIC;

const vehicleFieldSortValue = 'fitment-fields asc';

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsWithYearLast = (fields: FitmentField[]) => {
    const yearField = fields.find((field) => isStandardFitmentField(field, 'year'));
    return yearField
        ? [...fields.filter((field) => field !== yearField), yearField]
        : fields;
};

declare global {
    interface Window {
        shopify?: {
            idToken?: () => Promise<string>;
            toast?: { show?: (msg: string | { message: string; duration?: number }) => void };
        };
    }
}

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);

const VehiclesPage: React.FC = () => {
    const navigate = useNavigate();
    const { vehicleStore } = useStore();
    const { vehicles, loadVehicles, page, hasNext, totalCount, loadingInitial } = vehicleStore;
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const [vehicleCatalogLabel, setVehicleCatalogLabel] = useState(() => getStoredFitmentCatalogLabel() ?? "");

    const visibleFitmentFields = useMemo(
        () => getVisibleFitmentFields(fitmentCatalog),
        [fitmentCatalog],
    );
    const vehicleListFitmentFields = useMemo(
        () => orderFitmentFieldsWithYearLast(visibleFitmentFields),
        [visibleFitmentFields],
    );
    const legacyVisibleFields = useMemo(
        () => visibleFitmentFields.filter((field) => getLegacyFieldStateKey(field)),
        [visibleFitmentFields],
    );
    const fieldEnabled = useMemo(() => {
        const enabled = new Set(legacyVisibleFields.map((field) => getLegacyFieldStateKey(field)).filter(Boolean));
        return {
            year: enabled.has('year'),
            make: enabled.has('make'),
            model: enabled.has('model'),
            submodel: enabled.has('submodel'),
        };
    }, [legacyVisibleFields]);

    // ---------- Modal open/close ----------
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingIsCustom, setEditingIsCustom] = useState(false);
    const [persistFitments, setPersistFitments] = useState(true);

    const openAdd = useCallback(() => {
        setEditingId(null);
        setEditingIsCustom(false);
        setPersistFitments(true);
        setModalOpen(true);
    }, []);

    const onEditClick = useCallback((v: Vehicle) => {
        setEditingId(v.id);
        setEditingIsCustom(!!v.isCustom);
        setYearFrom(v.field1);
        setYearTo(v.field1);
        setMake(v.field2);
        setModel(v.field3);
        setSubmodel(v.field4);
        setPersistFitments(true);
        setErrors({});
        setSubmitError(undefined);
        setModalOpen(true);
    }, []);

    // ---------- Suggestions (replace with store/API as needed) ----------
    const makeSuggestions = useMemo(
        () => ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Nissan', 'Hyundai', 'Kia'],
        [],
    );

    const modelSuggestions = useMemo(
        () => ['Camry', 'Civic', 'F-150', 'Silverado', '3 Series', 'C-Class', 'Golf', 'Altima', 'Elantra', 'Sorento'],
        [],
    );

    const submodelSuggestions = useMemo(
        () => ['Base', 'SE', 'LE', 'XLE', 'Sport', 'Limited', 'Touring', 'Premium'],
        [],
    );

    // ---------- Form state (modal) ----------
    const [yearFrom, setYearFrom] = useState('');
    const [yearTo, setYearTo] = useState('');
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [submodel, setSubmodel] = useState('');

    const [makeSuggestion, setMakeSuggestion] = useState<string | undefined>();
    const [modelSuggestion, setModelSuggestion] = useState<string | undefined>();
    const [submodelSuggestion, setSubmodelSuggestion] = useState<string | undefined>();

    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);
    const [lastAdded, setLastAdded] = useState<VehicleCreate | null>(null);
    const [lastAddedExists, setLastAddedExists] = useState(false);

    const clampDigits = (val: string) => val.replace(/[^\d]/g, '').slice(0, 4);

    const findSuggestion = (val: string, list: string[]) =>
        val ? list.find(s => s.toLowerCase().startsWith(val.toLowerCase())) : undefined;

    const onYearFromChange = useCallback((v: string) => {
        const val = clampDigits(v);
        setYearFrom(val);
        setErrors(prev => ({ ...prev, yearFrom: undefined })); // clear field error on edit
    }, []);

    const onYearToChange = useCallback((v: string) => {
        const val = clampDigits(v);
        setYearTo(val);
        setErrors(prev => ({ ...prev, yearTo: undefined }));
    }, []);

    const onMakeChange = useCallback((v: string) => {
        setMake(v);
        setMakeSuggestion(findSuggestion(v, makeSuggestions));
    }, [makeSuggestions]);

    const onModelChange = useCallback((v: string) => {
        setModel(v);
        setModelSuggestion(findSuggestion(v, modelSuggestions));
    }, [modelSuggestions]);

    const onSubmodelChange = useCallback((v: string) => {
        setSubmodel(v);
        setSubmodelSuggestion(findSuggestion(v, submodelSuggestions));
    }, [submodelSuggestions]);

    const acceptSuggestion = (current: string, suggestion?: string) =>
        suggestion && suggestion.toLowerCase().startsWith(current.toLowerCase()) ? suggestion : current;

    const onKeyDownApplySuggestion = useCallback(
        (setter: (v: string) => void, current: string, suggestion?: string) =>
            (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === 'Tab') setter(acceptSuggestion(current, suggestion));
            },
        [],
    );

    const validate = (): boolean => {
        const e: FieldErrors = {};

        // Required checks follow the active field configuration. Disabled fields
        // stay editable in settings, but they should not block this working UI.
        if (fieldEnabled.year && !yearFrom.trim()) e.yearFrom = 'Start year is required';
        if (fieldEnabled.make && !make.trim()) e.make = 'Make is required';
        if (fieldEnabled.model && !model.trim()) e.model = 'Model is required';

        // Parse ints only if present
        const hasFrom = fieldEnabled.year && !!yearFrom;
        const hasTo = fieldEnabled.year && !!yearTo;
        const yf = hasFrom && isFourDigits(yearFrom) ? parseInt(yearFrom, 10) : NaN;
        const yt = hasTo && isFourDigits(yearTo) ? parseInt(yearTo, 10) : NaN;

        // Format checks (4 digits)
        if (hasFrom && !isFourDigits(yearFrom)) {
            e.yearFrom = 'Enter a 4-digit year (e.g., 2018)';
        }

        if (hasTo && !isFourDigits(yearTo)) {
            e.yearTo = 'Enter a 4-digit year (e.g., 2024)';
        }

        // Bounds (only if 4-digit parse is OK)
        if (!e.yearFrom && hasFrom && (Number.isNaN(yf) || !inYearBounds(yf))) {
            e.yearFrom = `Year must be between ${YEAR_MIN} and ${YEAR_MAX_DYNAMIC}`;
        }

        if (!e.yearTo && hasTo && (Number.isNaN(yt) || !inYearBounds(yt))) {
            e.yearTo = `Year must be between ${YEAR_MIN} and ${YEAR_MAX_DYNAMIC}`;
        }

        // Relationship: if yearTo present, yearFrom must be present
        if (!e.yearTo && hasTo && !hasFrom) {
            e.yearTo = 'Provide a start year first';
        }

        // Relationship: from <= to (only if both valid so far)
        if (!e.yearFrom && !e.yearTo && hasFrom && hasTo && yf > yt) {
            e.yearTo = 'End year must be greater than or equal to start year';
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const resetForm = useCallback(() => {
        setYearFrom('');
        setYearTo('');
        setMake('');
        setModel('');
        setSubmodel('');
        setMakeSuggestion(undefined);
        setModelSuggestion(undefined);
        setSubmodelSuggestion(undefined);
        setErrors({});
        setSubmitError(undefined);
    }, []);

    const handleClose = useCallback(() => {
        resetForm();
        setLastAdded(null);
        setLastAddedExists(false);
        setEditingId(null);
        setEditingIsCustom(false);
        setPersistFitments(true);
        setModalOpen(false);
    }, [resetForm]);

    const onCopyLast = useCallback(() => {
        if (!lastAdded) return;

        setYearFrom(lastAdded.yearFrom.toString());
        setYearTo(lastAdded.yearTo.toString());
        setMake(lastAdded.make);
        setModel(lastAdded.model);
        setSubmodel(lastAdded.submodel || '');
        setLastAdded(null);
        setLastAddedExists(false);
    }, [lastAdded]);

    const lastAddedLabel = useMemo(() => {
        if (!lastAdded) return '';

        const range = lastAdded.yearFrom === lastAdded.yearTo
            ? `${lastAdded.yearFrom}`
            : `${lastAdded.yearFrom}-${lastAdded.yearTo}`;

        return `${lastAdded.make}, ${lastAdded.model}${lastAdded.submodel ? `, ${lastAdded.submodel}` : ''}, ${range}`;
    }, [lastAdded]);

    const onSave = async () => {
        setSubmitError(undefined);
        if (!validate()) return;

        setSubmitting(true);
        const normalizedYearFrom = fieldEnabled.year ? parseInt(yearFrom, 10) : 9999;
        const normalizedYearTo = fieldEnabled.year ? parseInt(yearTo || yearFrom, 10) : normalizedYearFrom;

        const basePayload: VehicleCreate = {
            yearFrom: normalizedYearFrom,
            yearTo: normalizedYearTo,
            make: fieldEnabled.make ? make.trim() : '',
            model: fieldEnabled.model ? model.trim() : '',
            submodel: fieldEnabled.submodel ? (submodel.trim() || null) : null,
        };

        try {
            if (editingId) {
                const payload: VehicleEdit = { ...basePayload, isCustom: editingIsCustom, persistFitments };
                await vehicleStore.update(editingId, payload);

                await loadVehicles(
                    1,
                    queryValue,
                    selectedSource[0] || '',
                    sortSelected[0],
                    selectedYear,
                    selectedMake,
                    selectedModel,
                    selectedAssigned[0] || '',
                    selectedFitmentValues,
                );

                showToast('Vehicle saved');
            } else {
                await vehicleStore.create(basePayload);

                await loadVehicles(
                    1,
                    queryValue,
                    selectedSource[0] || '',
                    sortSelected[0],
                    selectedYear,
                    selectedMake,
                    selectedModel,
                    selectedAssigned[0] || '',
                    selectedFitmentValues,
                );

                showToast('Vehicle added');
                setLastAdded(basePayload);
                setLastAddedExists(false);
                resetForm();
            }
        } catch (err: any) {
            if (!editingId && err?.response?.status === 409) {
                await loadVehicles(
                    1,
                    queryValue,
                    selectedSource[0] || '',
                    sortSelected[0],
                    selectedYear,
                    selectedMake,
                    selectedModel,
                    selectedAssigned[0] || '',
                    selectedFitmentValues,
                );

                showToast('Vehicle already exists');
                setLastAdded(basePayload);
                setLastAddedExists(true);
                resetForm();
            } else {
                setSubmitError(err?.message ?? (editingId ? 'Failed to save vehicle' : 'Failed to create vehicle'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- Existing list logic ----------
    const [queryValue, setQueryValue] = useState('');
    const [selectedSource, setSelectedSource] = useState<string[]>([]);
    const [selectedAssigned, setSelectedAssigned] = useState<string[]>([]);
    const [sortSelected, setSortSelected] = useState<string[]>([vehicleFieldSortValue]);
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMake, setSelectedMake] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedAdditionalFields, setSelectedAdditionalFields] = useState<FitmentFieldValues>({});
    const [fieldOptions, setFieldOptions] = useState<Record<string, SelectOption[]>>({});
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const didInitialLoadRef = useRef(false);
    const selectedFitmentValues = useMemo(
        // Keep legacy state variables for existing sort/filter calls, but also
        // expose the full field map so custom selector fields can participate.
        () => normalizeFitmentFieldValues(vehicleListFitmentFields, {
            ...selectedAdditionalFields,
            year: selectedYear,
            make: selectedMake,
            model: selectedModel,
        }),
        [selectedAdditionalFields, selectedMake, selectedModel, selectedYear, vehicleListFitmentFields],
    );
    const hasVehicleDropdownFilter = hasAnyFitmentFieldValue(vehicleListFitmentFields, selectedFitmentValues);
    const storedFitmentValues = useMemo(
        () => normalizeFitmentFieldValues(vehicleListFitmentFields, {
            ...vehicleStore.fieldValues,
            year: vehicleStore.year,
            make: vehicleStore.make,
            model: vehicleStore.model,
        }),
        [vehicleListFitmentFields, vehicleStore.fieldValues, vehicleStore.year, vehicleStore.make, vehicleStore.model],
    );
    const hasStoredVehicleDropdownFilter = hasAnyFitmentFieldValue(vehicleListFitmentFields, storedFitmentValues);
    const hasActiveVehicleFilters =
        queryValue.trim().length > 0
        || selectedSource.some(Boolean)
        || selectedAssigned.some(Boolean)
        || hasVehicleDropdownFilter
        || vehicleStore.searchTerm.trim().length > 0
        || !!vehicleStore.source
        || !!vehicleStore.assigned
        || hasStoredVehicleDropdownFilter;

    const applySelectedFitmentValues = useCallback((nextValues: FitmentFieldValues) => {
        const normalized = normalizeFitmentFieldValues(vehicleListFitmentFields, nextValues);
        // Split the generic map back into legacy fields plus "everything else".
        // This keeps older vehicle APIs working while the UI can render 4-8
        // merchant-defined fields.
        setSelectedYear(normalized.year || '');
        setSelectedMake(normalized.make || '');
        setSelectedModel(normalized.model || '');
        setSelectedAdditionalFields(
            Object.entries(normalized)
                .filter(([key]) => key !== 'year' && key !== 'make' && key !== 'model')
                .reduce<FitmentFieldValues>((accumulator, [key, value]) => {
                    accumulator[key] = value;
                    return accumulator;
                }, {})
        );
    }, [vehicleListFitmentFields]);

    useEffect(() => {
        loadVehicles();
        didInitialLoadRef.current = true;
    }, [loadVehicles]);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) {
                    setFitmentCatalog(catalog);
                    setVehicleCatalogLabel(setCachedFitmentCatalogLabel(catalog.name));
                }
            } catch {
                // The field helper falls back to the default YMMS shape when
                // settings cannot be loaded, keeping the page usable.
                if (!cancelled) {
                    setFitmentCatalog(null);
                    setVehicleCatalogLabel(getCachedFitmentCatalogLabel());
                }
            }
        };

        const handleLabelChange = (event: Event) => {
            const nextLabel = (event as CustomEvent<string>).detail;
            if (nextLabel) setVehicleCatalogLabel(nextLabel);
        };

        window.addEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        loadFitmentCatalog();
        return () => {
            cancelled = true;
            window.removeEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        };
    }, []);

    useEffect(() => {
        if (!didInitialLoadRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            loadVehicles(
                1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [queryValue, selectedSource, selectedAssigned, sortSelected, selectedYear, selectedMake, selectedModel, selectedFitmentValues, loadVehicles]);

    useEffect(() => {
        // If settings disable a legacy field, clear its state so hidden values
        // do not continue filtering the vehicle list.
        if (!fieldEnabled.year) setSelectedYear('');
        if (!fieldEnabled.make) setSelectedMake('');
        if (!fieldEnabled.model) setSelectedModel('');
    }, [fieldEnabled.make, fieldEnabled.model, fieldEnabled.year]);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            if (vehicleListFitmentFields.length === 0) {
                setFieldOptions({});
                return;
            }

            setLoadingFieldOptions(true);
            try {
                // The admin selector uses every enabled field. Required/search rules
                // control validation and disabled states, not whether the field appears.
                const result = await vehicleStore.filterOptions(
                    selectedFitmentValues.year || '',
                    selectedFitmentValues.make || '',
                    selectedSource[0] || '',
                    selectedFitmentValues,
                );

                if (cancelled) return;

                const nextOptions = vehicleListFitmentFields.reduce<Record<string, SelectOption[]>>((accumulator, field) => {
                    const key = normalizeFitmentKey(field);
                    const values = result.optionsByField?.[key] ?? [];
                    accumulator[key] = [
                        { label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' },
                        ...values.map((value) => ({ label: value, value })),
                    ];
                    return accumulator;
                }, {});

                setFieldOptions(nextOptions);
            } catch {
                if (!cancelled) setFieldOptions({});
            } finally {
                if (!cancelled) setLoadingFieldOptions(false);
            }
        };

        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [selectedFitmentValues, selectedSource, vehicleStore, vehicleListFitmentFields]);

    // Vehicle dropdown filters are applied server-side so the list remains paged.
    // Do not pull all pages into the client; the API should return one page of
    // filtered results at a time.

    const handleQueryChange = useCallback((v: string) => setQueryValue(v), []);
    const handleQueryClear = useCallback(() => setQueryValue(''), []);
    const onSourceChange = useCallback((v: string[]) => setSelectedSource(v), []);
    const onAssignedChange = useCallback((v: string[]) => setSelectedAssigned(v), []);

    const sortOptions: IndexFiltersProps['sortOptions'] = [
        { label: 'Fitment fields', value: vehicleFieldSortValue, directionLabel: 'Ascending' },
        { label: 'Fitment fields', value: 'fitment-fields desc', directionLabel: 'Descending' },
        { label: 'Assigned products', value: 'assigned asc', directionLabel: 'Lowest to highest' },
        { label: 'Assigned products', value: 'assigned desc', directionLabel: 'Highest to lowest' },
    ];

    const handleSort = useCallback((v: string[]) => setSortSelected(v), []);
    const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);

    const onClearAll = useCallback(() => {
        setQueryValue('');
        setSelectedSource([]);
        setSelectedAssigned([]);
        setSortSelected([vehicleFieldSortValue]);
        setSelectedYear('');
        setSelectedMake('');
        setSelectedModel('');
        setSelectedAdditionalFields({});

        loadVehicles(1, '', '', vehicleFieldSortValue, '', '', '', '', {});
    }, [loadVehicles]);

    const tabs: TabProps[] = [{ id: 'all', content: 'All' }];
    const [selected, setSelected] = useState(0);

    const hasPrevious = page > 1;

    const onNext = useCallback(() => {
        if (hasNext) {
            loadVehicles(
                page + 1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        }
    }, [hasNext, loadVehicles, page, queryValue, selectedSource, selectedAssigned, sortSelected, selectedYear, selectedMake, selectedModel, selectedFitmentValues]);

    const onPrevious = useCallback(() => {
        if (hasPrevious) {
            loadVehicles(
                page - 1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        }
    }, [hasPrevious, loadVehicles, page, queryValue, selectedSource, selectedAssigned, sortSelected, selectedYear, selectedMake, selectedModel, selectedFitmentValues]);

    const filteredVehicles = useMemo(() => {
        return Array.isArray(vehicles) ? vehicles : [];
    }, [vehicles]);

    const rows: VehicleRow[] = filteredVehicles.slice() as VehicleRow[];
    const showCreateVehicleEmptyState =
        didInitialLoadRef.current
        && !loadingInitial
        && rows.length === 0
        && totalCount === 0
        && !hasActiveVehicleFilters;
    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState<VehicleRow>(rows, { resourceIDResolver: (v) => String(v.id) });
    const isCondensed = useBreakpoints().smDown;
    const openAssignedProducts = useCallback((vehicle: Vehicle) => {
        const params = new URLSearchParams();
        const fieldValues = vehicleListFitmentFields.reduce<FitmentFieldValues>((accumulator, field) => {
            const key = normalizeFitmentKey(field);
            const value = getLegacyVehicleFieldValue(vehicle, field);
            if (key && value && value !== '-') {
                accumulator[key] = value;
            }
            return accumulator;
        }, {});
        const year = fieldValues.year || '';
        const make = fieldValues.make || '';
        const model = fieldValues.model || '';

        if (year) params.set('year', year);
        if (make) params.set('make', make);
        if (model) params.set('model', model);
        if (Object.keys(fieldValues).length > 0) {
            params.set('fieldValues', JSON.stringify(fieldValues));
        }
        params.set('mappedOnly', 'true');

        navigate(`/products?${params.toString()}`);
    }, [navigate, vehicleListFitmentFields]);
    // ---------- BULK ACTIONS (Delete modal + Merge) ----------
    const selectedCount = allResourcesSelected ? totalCount : selectedResources.length;

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState<{ hasCustom: boolean; hasStandard: boolean; customCount: number }>({
        hasCustom: false,
        hasStandard: false,
        customCount: 0,
    });

    const onBulkMerge = useCallback(async () => {
        const count = allResourcesSelected ? totalCount : selectedResources.length;

        if (count < 2) {
            showToast('Select 2 or more vehicles to merge');
            return;
        }

        const defaultTarget = !allResourcesSelected && selectedResources.length > 0 ? selectedResources[0] : '';
        const targetInput = window.prompt('Enter target Vehicle ID to keep (others will be merged into this):', defaultTarget || '');
        const targetId = targetInput ? Number(targetInput) : NaN;

        if (!targetId || Number.isNaN(targetId)) return;

        try {
            if (typeof (vehicleStore as any).mergeVehicles === 'function') {
                await (vehicleStore as any).mergeVehicles({
                    targetId,
                    ids: allResourcesSelected ? null : selectedResources.map((id) => Number(id)),
                    scope: allResourcesSelected ? {
                        query: queryValue,
                        source: selectedSource[0] || '',
                        sort: sortSelected[0],
                        year: selectedYear,
                        make: selectedMake,
                        model: selectedModel,
                        fieldValues: selectedFitmentValues,
                        assigned: selectedAssigned[0] || '',
                    } : undefined,
                });
            } else {
                showToast('Merge not implemented');
                return;
            }

            showToast('Vehicles merged');
            clearSelection();

            await loadVehicles(
                1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        } catch (e: any) {
            showToast(e?.message ?? 'Merge failed');
        }
    }, [
        allResourcesSelected,
        totalCount,
        selectedResources,
        vehicleStore,
        clearSelection,
        loadVehicles,
        queryValue,
        selectedSource,
        selectedAssigned,
        sortSelected,
        selectedYear,
        selectedMake,
        selectedModel,
        selectedFitmentValues,
    ]);

    const onBulkDeleteClick = useCallback(() => {
        const count = allResourcesSelected ? totalCount : selectedResources.length;
        if (count < 1) return;

        if (allResourcesSelected) {
            const hasStandard = selectedSource[0] !== 'Custom';
            const hasCustom = selectedSource[0] !== 'System';
            setDeleteInfo({ hasStandard, hasCustom, customCount: hasCustom ? count : 0 });
        } else {
            const selectedVehicles = rows.filter((v) => selectedResources.includes(String(v.id)));
            const customVehicles = selectedVehicles.filter((v) => v.isCustom);

            setDeleteInfo({
                hasStandard: selectedVehicles.length > customVehicles.length,
                hasCustom: customVehicles.length > 0,
                customCount: customVehicles.length,
            });
        }

        setDeleteOpen(true);
    }, [allResourcesSelected, totalCount, selectedResources, rows, selectedSource]);

    const confirmDelete = useCallback(async () => {
        setDeleting(true);

        try {
            if (typeof (vehicleStore as any).bulkDelete === 'function') {
                if (allResourcesSelected) {
                    await (vehicleStore as any).bulkDelete({
                        ids: null,
                        scope: {
                            query: queryValue,
                            source: 'Custom',
                            sort: sortSelected[0],
                            year: selectedYear,
                            make: selectedMake,
                            model: selectedModel,
                            fieldValues: selectedFitmentValues,
                            assigned: selectedAssigned[0] || '',
                        },
                    });
                } else {
                    const ids = rows
                        .filter((v) => selectedResources.includes(String(v.id)) && v.isCustom)
                        .map((v) => Number(v.id));

                    await (vehicleStore as any).bulkDelete({ ids });
                }
            } else {
                const ids = allResourcesSelected
                    ? []
                    : rows
                        .filter((v) => selectedResources.includes(String(v.id)) && v.isCustom)
                        .map((v) => Number(v.id));

                await Promise.all(ids.map((id) => (vehicleStore as any).delete?.(id)));
            }

            showToast('Vehicles deleted');
            setDeleteOpen(false);
            clearSelection();

            await loadVehicles(
                1,
                queryValue,
                selectedSource[0] || '',
                sortSelected[0],
                selectedYear,
                selectedMake,
                selectedModel,
                selectedAssigned[0] || '',
                selectedFitmentValues,
            );
        } catch (e: any) {
            showToast(e?.message ?? 'Failed to delete');
        } finally {
            setDeleting(false);
        }
    }, [
        allResourcesSelected,
        selectedResources,
        rows,
        vehicleStore,
        clearSelection,
        loadVehicles,
        queryValue,
        selectedSource,
        selectedAssigned,
        sortSelected,
        selectedYear,
        selectedMake,
        selectedModel,
        selectedFitmentValues,
    ]);

    const promotedBulkActions = [
        { content: 'Merge', onAction: onBulkMerge, disabled: selectedCount < 2 },
    ];

    const bulkActions = [
        { icon: DeleteIcon, content: 'Delete', destructive: true, onAction: onBulkDeleteClick, disabled: selectedCount < 1 },
    ];

    return (
        <>
            {/* App Bridge React Modal with local Polaris AppProvider so portal components work */}
            <Modal id="add-vehicle" open={modalOpen} onHide={handleClose}>
                <AppProvider i18n={enTranslations}>
                    <div style={{ padding: 16 }} onInput={() => { if (lastAdded) { setLastAdded(null); setLastAddedExists(false); } }}>
                        {submitError && (
                            <Banner tone="critical">{submitError}</Banner>
                        )}

                        {editingId && !editingIsCustom && (
                            <div style={{ backgroundColor: '#f6f6f7', padding: 12, marginTop: 8, marginBottom: 16 }}>
                                <Text as="p">This is a system vehicle and cannot be edited. Saving will create a new vehicle.</Text>
                                <Checkbox
                                    label="Persist saved fitments"
                                    checked={persistFitments}
                                    onChange={(v: boolean) => setPersistFitments(v)}
                                />
                            </div>
                        )}

                        <FormLayout>
                            {!editingId && lastAdded && (
                                <FormLayout.Group>
                                    <MiniBanner
                                        tone={lastAddedExists ? 'info' : 'success'}
                                        title={lastAddedExists ? 'Vehicle already exists' : 'Vehicle added'}
                                        onDismiss={() => { setLastAdded(null); setLastAddedExists(false); }}
                                        actions={[
                                            { content: 'Duplicate', onAction: onCopyLast },
                                        ]}
                                    >
                                        <b>{lastAddedLabel}</b>
                                    </MiniBanner>
                                </FormLayout.Group>
                            )}

                            {fieldEnabled.year && (
                                <FormLayout.Group condensed>
                                    <TextField
                                        label={legacyVisibleFields.find((field) => getLegacyFieldStateKey(field) === 'year')?.label ?? 'Year'}
                                        value={yearFrom}
                                        onChange={onYearFromChange}
                                        placeholder="e.g. 2010"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        error={errors.yearFrom}
                                    />
                                    <TextField
                                        label="End year"
                                        value={yearTo}
                                        onChange={onYearToChange}
                                        placeholder="e.g. 2018"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        error={errors.yearTo}
                                    />
                                </FormLayout.Group>
                            )}

                            {fieldEnabled.make && (
                                <div onKeyDown={onKeyDownApplySuggestion(setMake, make, makeSuggestion)}>
                                    <TextField
                                        label={legacyVisibleFields.find((field) => getLegacyFieldStateKey(field) === 'make')?.label ?? 'Make'}
                                        value={make}
                                        onChange={onMakeChange}
                                        autoComplete="off"
                                        placeholder="e.g. Toyota"
                                        suggestion={makeSuggestion}
                                        error={errors.make}
                                    />
                                </div>
                            )}

                            {fieldEnabled.model && (
                                <div onKeyDown={onKeyDownApplySuggestion(setModel, model, modelSuggestion)}>
                                    <TextField
                                        label={legacyVisibleFields.find((field) => getLegacyFieldStateKey(field) === 'model')?.label ?? 'Model'}
                                        value={model}
                                        onChange={onModelChange}
                                        autoComplete="off"
                                        placeholder="e.g. Camry"
                                        suggestion={modelSuggestion}
                                        error={errors.model}
                                    />
                                </div>
                            )}

                            {fieldEnabled.submodel && (
                                <div onKeyDown={onKeyDownApplySuggestion(setSubmodel, submodel, submodelSuggestion)}>
                                    <TextField
                                        label={`${legacyVisibleFields.find((field) => getLegacyFieldStateKey(field) === 'submodel')?.label ?? 'Submodel'} (optional)`}
                                        value={submodel}
                                        onChange={onSubmodelChange}
                                        autoComplete="off"
                                        placeholder="e.g. SE"
                                        suggestion={submodelSuggestion}
                                    />
                                </div>
                            )}

                            {errors.make && errors.model && (
                                <InlineError message="Make and Model are required" fieldID="make" />
                            )}
                        </FormLayout>
                    </div>
                </AppProvider>

                <TitleBar
                    title={editingId ? (editingIsCustom ? 'Edit Vehicle' : 'Edit System Vehicle') : 'Add Vehicle'}>
                    <button onClick={handleClose} disabled={submitting} style={{ marginRight: 8 }}>Close</button>
                    <button variant="primary" onClick={onSave} disabled={submitting} style={{ marginRight: 8 }}>Save</button>
                </TitleBar>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal id="delete-vehicles" open={deleteOpen} onHide={() => setDeleteOpen(false)}>
                <AppProvider i18n={enTranslations}>
                    <div style={{ padding: 16 }}>
                        <MiniBanner tone="critical" title="Delete vehicles">
                            <p style={{ marginTop: 8 }}>
                                {deleteInfo.hasStandard && !deleteInfo.hasCustom && (
                                    <>System vehicle data cannot be deleted.</>
                                )}
                                {deleteInfo.hasStandard && deleteInfo.hasCustom && (
                                    <>System vehicle data cannot be deleted. Only user-added vehicles (and their associated fitments) will be deleted. This action cannot be undone.</>
                                )}
                                {!deleteInfo.hasStandard && deleteInfo.hasCustom && (
                                    allResourcesSelected
                                        ? <>You’re about to delete <b>ALL {totalCount}</b> vehicles (and their associated fitments) that match the current filters. This cannot be undone.</>
                                        : <>You’re about to delete <b>{deleteInfo.customCount}</b> selected vehicle(s) (and their associated fitments). This cannot be undone.</>
                                )}
                            </p>
                        </MiniBanner>
                    </div>
                </AppProvider>

                <TitleBar title="Delete vehicles">
                    <button onClick={() => setDeleteOpen(false)} disabled={deleting}>Close</button>
                    <button variant="primary" onClick={confirmDelete} disabled={deleting || !deleteInfo.hasCustom}>Delete</button>
                </TitleBar>
            </Modal>

            <Page
                fullWidth
                title={vehicleCatalogLabel || "\u00a0"}
                primaryAction={{ content: 'Add vehicle', onAction: openAdd }}
                secondaryActions={[
                    {
                        content: 'Manage fields',
                        onAction: () => navigate('/storefront?section=selector-fields'),
                    },
                ]}
            >
                <Card>
                    <IndexFilters
                        sortOptions={sortOptions}
                        sortSelected={sortSelected}
                        onSort={handleSort}
                        loading={loadingInitial}
                        tabs={tabs}
                        selected={selected}
                        onSelect={setSelected}
                        queryValue={queryValue}
                        queryPlaceholder="Search vehicles"
                        onQueryChange={handleQueryChange}
                        onQueryClear={handleQueryClear}
                        filters={[
                            {
                                key: 'source',
                                label: 'Data source',
                                shortcut: true,
                                filter: (
                                    <ChoiceList
                                        title="Source"
                                        titleHidden
                                        choices={[
                                            { label: 'System', value: 'System' },
                                            { label: 'User-added', value: 'Custom' },
                                            { label: 'ATD', value: 'ATD' },
                                            { label: 'Turn14', value: 'Turn14' },
                                        ]}
                                        selected={selectedSource}
                                        onChange={onSourceChange}
                                        allowMultiple={false}
                                    />
                                ),
                            },
                            {
                                key: 'assigned',
                                label: 'Assigned products',
                                shortcut: true,
                                filter: (
                                    <ChoiceList
                                        title="Assigned products"
                                        titleHidden
                                        choices={[
                                            { label: 'With assigned products', value: 'with' },
                                            { label: 'Without assigned products', value: 'without' },
                                        ]}
                                        selected={selectedAssigned}
                                        onChange={onAssignedChange}
                                        allowMultiple={false}
                                    />
                                ),
                            },
                        ]}
                        appliedFilters={
                            [
                                ...(selectedSource.length
                                    ? [{
                                        key: 'source',
                                        label: `Source: ${selectedSource[0] === 'Custom' ? 'User-added' : selectedSource[0]}`,
                                        onRemove: () => setSelectedSource([])
                                    }]
                                    : []),
                                ...(selectedAssigned.length
                                    ? [{
                                        key: 'assigned',
                                        label: selectedAssigned[0] === 'with'
                                            ? 'Assigned products: With assigned products'
                                            : 'Assigned products: Without assigned products',
                                        onRemove: () => setSelectedAssigned([])
                                    }]
                                    : []),
                            ]
                        }
                        onClearAll={onClearAll}
                        mode={mode}
                        setMode={setMode}
                    />

                    <div className="pf-fitment-selector">
                        {vehicleListFitmentFields.map((field) => {
                            const key = normalizeFitmentKey(field);
                            return (
                                <div key={key} className="pf-fitment-selector__field">
                                    <Select
                                        label={field.label}
                                        options={fieldOptions[key] ?? [{ label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' }]}
                                        value={getFitmentFieldValue(selectedFitmentValues, field)}
                                        disabled={isFitmentFieldDisabled(vehicleListFitmentFields, selectedFitmentValues, field, loadingFieldOptions)}
                                        onChange={(value) => {
                                            // updateFitmentFieldValue also clears downstream
                                            // selections when an earlier dropdown changes.
                                            const nextValues = updateFitmentFieldValue(vehicleListFitmentFields, selectedFitmentValues, field, value);
                                            applySelectedFitmentValues(nextValues);

                                            loadVehicles(
                                                1,
                                                queryValue,
                                                selectedSource[0] || '',
                                                sortSelected[0],
                                                nextValues.year || '',
                                                nextValues.make || '',
                                                nextValues.model || '',
                                                selectedAssigned[0] || '',
                                                normalizeFitmentFieldValues(vehicleListFitmentFields, nextValues),
                                            );
                                        }}
                                    />
                                </div>
                            );
                        })}

                        {vehicleListFitmentFields.length > 0 && (
                            <div className="pf-fitment-selector__action">
                                <Button
                                    onClick={() => {
                                        applySelectedFitmentValues({});

                                        loadVehicles(
                                            1,
                                            queryValue,
                                            selectedSource[0] || '',
                                            sortSelected[0],
                                            '',
                                            '',
                                            '',
                                            selectedAssigned[0] || '',
                                            {},
                                        );
                                    }}
                                    disabled={!hasVehicleDropdownFilter}
                                >
                                    Clear
                                </Button>
                            </div>
                        )}
                    </div>

                    <IndexTable
                        condensed={isCondensed}
                        resourceName={{ singular: 'vehicle', plural: 'vehicles' }}
                        itemCount={rows.length}
                        selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                        onSelectionChange={handleSelectionChange}
                        bulkActions={bulkActions}
                        promotedBulkActions={promotedBulkActions}
                        emptyState={showCreateVehicleEmptyState ? (
                            <div className="pf-vehicle-empty-state">
                                <img
                                    className="pf-vehicle-empty-state__image"
                                    src={vehicleEmptyStateImage}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <Text as="h2" variant="headingMd">
                                    Add your first vehicle
                                </Text>
                                <div className="pf-vehicle-empty-state__copy">
                                    <Text as="p" tone="subdued">
                                        Add vehicles manually or import them from a file to start your fitment catalog.
                                    </Text>
                                </div>
                                <div className="pf-vehicle-empty-state__actions">
                                    <Button variant="secondary" onClick={() => navigate('/imports')}>
                                        Import vehicles
                                    </Button>
                                    <Button variant="primary" onClick={openAdd}>
                                        Add vehicle
                                    </Button>
                                </div>
                            </div>
                        ) : undefined}
                        headings={[
                            ...vehicleListFitmentFields.map((field) => ({ title: field.label })),
                            { title: 'Assigned Products' },
                            { title: '' },
                        ] as any}
                        pagination={{ hasNext, hasPrevious, onNext, onPrevious }}
                    >
                        {filteredVehicles.map((v: any, index: number) => (
                            <IndexTable.Row
                                id={String(v.id)}
                                key={v.id}
                                selected={selectedResources.includes(String(v.id))}
                                position={index}
                            >
                                {vehicleListFitmentFields.map((field) => (
                                    <IndexTable.Cell key={normalizeFitmentKey(field)}>
                                        <Text as="span">{getLegacyVehicleFieldValue(v, field) || '-'}</Text>
                                    </IndexTable.Cell>
                                ))}

                                {/* Assigned Products as a button (placeholder; no actual nav yet) */}
                                <IndexTable.Cell>
                                    <Button
                                        variant="secondary"
                                        onClick={() => openAssignedProducts(v)}
                                        accessibilityLabel={`Open assigned products for ${vehicleListFitmentFields.map((field) => getLegacyVehicleFieldValue(v, field)).filter(Boolean).join(' ')}`}
                                    >
                                        {v.assignedProducts?.toString?.() ?? '0'}
                                    </Button>
                                </IndexTable.Cell>

                                <IndexTable.Cell>
                                    <Button variant="tertiary" onClick={() => onEditClick(v)}>
                                        Edit
                                    </Button>
                                </IndexTable.Cell>
                            </IndexTable.Row>
                        ))}
                    </IndexTable>
                </Card>
            </Page>
        </>
    );
};

export default observer(VehiclesPage);
